import { ModalRef, sendNotification, useTranslation } from '@msdk/common'
import { CustomActionPosition, Entity } from '@msdk/definitions'
import { ButtonSecondary, COLORS, IconSelector, Pagination, styled, TableWrapper } from '@msdk/ui'
import React from 'react'

import { useFormModal, useModalView, useViewActionButtons } from '../../hooks'
import { stringFormat } from '../../utils'
import { DynamicView } from '../DynamicView'
import { DynamicViewProvider } from '../DynamicView/context'
import { Filters } from '../Filters'
import { ViewActions } from '../ViewActions'

const Container = styled('div')`
  display: flex;
  flex-direction: column;
`

const Wrapper = styled('div')`
  padding: 16px 24px;
`

const Header = styled('div')`
  width: 100%;
  height: 64px;
  background-color: ${COLORS.grey50};
  display: flex;
  flex-direction: row;
`

const HeaderText = styled('p')`
  padding: 0 16px;
  font-weight: 600 !important;
  font-size: 16px !important;
  line-height: 24px !important;
`

const CloseContainer = styled('span')`
  padding-top: 15px;
  cursor: pointer;
  margin-left: auto;
  margin-right: 8px;
`

const StyledTableWrapper = styled(TableWrapper)`
  display: flex;
  align-items: flex-end;
  flex-direction: column;
`

const PaginationWrapper = styled('div')`
  display: flex;
`

const StyledPagination = styled(Pagination)`
  margin-left: auto;
`

const ButtonWrapper = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: row;
  margin: 0 0 10px;
  padding: 0;
`

const InnerActionsWrapper = styled('div')`
  margin-left: auto;
`

const CreateButton = styled(ButtonSecondary)`
  margin-left: auto;
`

const FiltersWrapper = styled('div')`
  width: 100%;
  padding-top: 8px;
  padding-bottom: 24px;
`

interface Props {
  entityName: string
  viewName: string
  onClose: () => void
  modalRef: ModalRef
}

export function DrawerView(props: Props) {
  const t = useTranslation()
  const { createModal: createFormModal } = useFormModal()

  const {
    entityModel,
    viewModel,
    isLoading,
    response,
    currentPage,
    acl,
    filterChange,
    onPageChange,
    fetchData,
    onOrderChange,
    reload,
    selectedRows,
    onRowSelected,
    currentFilters,
    isMultiselected,
    setIsMultiselect,
    additionalCondition,
    addColumnFilter,
    removeColumnFilter,
    removeAllColumnFilter,
    currentOrder,
    addOrUpdateFilter,
  } = useModalView(props.entityName, props.viewName, false)

  const onRowClicked = (row: Entity) => {
    const formToOpen = viewModel.openFormModel ?? entityModel.defaultForm
    if (formToOpen) {
      const modalRef = createFormModal(
        entityModel.name,
        formToOpen,
        row.id,
        () => {
          fetchData()
        },
        { mode: 'Readonly', modalForm: viewModel.modalForm },
      )
      modalRef.open()
    } else {
      sendNotification({
        message:
          'Missing DefaultForm in entity model and no OpenFormModel was specified in the view, can not determine the form to open!',
        status: 'error',
      })
    }
  }

  const onCreate = () => {
    const modalRef = createFormModal(
      entityModel.name,
      entityModel.createForm ?? entityModel.editForm ?? entityModel.defaultForm,
      'Create',
      () => {
        fetchData()
      },
      { mode: 'Edit', modalForm: viewModel.modalForm },
    )
    modalRef.open()
  }

  const tryGetCustomCreateButtonLabel = () => {
    const { labels } = viewModel
    if (labels?.createButton) {
      return t(labels.createButton.idml, labels.createButton.description)
    }
  }

  const insideActions = useViewActionButtons(viewModel, CustomActionPosition.InsideView, reload, undefined, acl)
  const outsideActions = useViewActionButtons(viewModel, CustomActionPosition.OutsideView, reload, undefined, acl)

  return (
    <>
      {viewModel && (
        <Container>
          <Header>
            <HeaderText>{t(viewModel.idml, viewModel.description)}</HeaderText>
            <CloseContainer>
              <IconSelector iconName="Close" color="inherit" onClick={props.onClose} />
              <ViewActions
                actions={outsideActions}
                entityName={viewModel.entity}
                viewName={viewModel.name}
                reload={reload}
                currentFilters={currentFilters}
                acl={acl}
              />
            </CloseContainer>
          </Header>
          <Wrapper>
            <ButtonWrapper>
              <InnerActionsWrapper>
                <ViewActions
                  actions={insideActions}
                  entityName={viewModel.entity}
                  viewName={viewModel.name}
                  reload={reload}
                  currentFilters={currentFilters}
                  acl={acl}
                />
              </InnerActionsWrapper>
              {entityModel.canBeCreated && viewModel.canBeCreated && acl?.canCreate && (
                <CreateButton onClick={onCreate}>
                  <IconSelector iconName="Add" color="primary" />
                  {tryGetCustomCreateButtonLabel() ??
                    stringFormat(t('CMPSR_CREATE_ENTITY', 'Create {0}'), t(entityModel.idml, entityModel.description))}
                </CreateButton>
              )}
            </ButtonWrapper>
            <FiltersWrapper>
              <Filters
                viewName={viewModel.name}
                filters={viewModel.filters}
                onFilterChange={filterChange}
                multiselect={isMultiselected}
                setMultiselect={setIsMultiselect}
                acl={acl}
                entityModel={entityModel}
              />
            </FiltersWrapper>
            <StyledTableWrapper>
              <DynamicViewProvider
                entityModel={entityModel}
                viewModel={viewModel}
                reload={reload}
                rows={response?.source || []}
                isLoading={isLoading}
                onRowClick={onRowClicked}
                onOrderChange={onOrderChange}
                multiSelect={isMultiselected}
                selectedRows={selectedRows}
                onRowSelected={onRowSelected}
                acl={acl}
                currentFilters={currentFilters}
                additionalCondition={additionalCondition}
                addColumnFilter={addColumnFilter}
                removeColumnFilter={removeColumnFilter}
                removeAllColumnFilter={removeAllColumnFilter}
                currentOrder={currentOrder}
                modalRef={props.modalRef}
                addOrUpdateFilter={addOrUpdateFilter}
              >
                <DynamicView />
              </DynamicViewProvider>
              {!viewModel.hidePagination && (
                <PaginationWrapper>
                  <StyledPagination
                    count={response?.totalItems ?? 0}
                    page={currentPage}
                    rowsPerPage={response?.pageSize ?? 30}
                    rowsPerPageOptions={[]}
                    onPageChange={(_e, page) => onPageChange(page)}
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} ${t('CMPSR_PAGINATOR_OF', 'of')} ${count}`
                    }
                  />
                </PaginationWrapper>
              )}
            </StyledTableWrapper>
          </Wrapper>
        </Container>
      )}
    </>
  )
}
